// src/app/shared/system-roles.ts
// ⚠️ Keep in sync with ElikitaAuth.API.Models.SystemRoles (C#)

export const SystemRoles = {
  // Super Admin - can manage everything across all organizations
  SuperAdmin: 'superadmin',
  
  // Organization Admin - can manage users within their organization
  Admin: 'admin',
  
  // Generic Practitioner (used during registration, before specific role assignment)
  Practitioner: 'practitioner',
  
  // Specific Practitioner Roles (assigned during approval)
  ServiceProvider: 'serviceprovider',
  Doctor: 'doctor',
  Nurse: 'nurse',
  LabTechnician: 'labtechnician',
  Pharmacist: 'pharmacist',
  Receptionist: 'receptionist',
  Consultant: 'consultant',
  Cashier: 'cashier',
  
  // Patient Role
  Patient: 'patient'
} as const;

// Type-safe union of all role strings
export type RoleType = typeof SystemRoles[keyof typeof SystemRoles];

// Arrays for validation - MUST match C# backend
export const PRACTITIONER_ROLES = [
  SystemRoles.SuperAdmin,
  SystemRoles.Admin,
  SystemRoles.Practitioner,
  SystemRoles.ServiceProvider,
  SystemRoles.Doctor,
  SystemRoles.Nurse,
  SystemRoles.LabTechnician,
  SystemRoles.Pharmacist,
  SystemRoles.Receptionist,
  SystemRoles.Consultant,
  SystemRoles.Cashier
] as const;

export const PATIENT_ROLES = [SystemRoles.Patient] as const;

// 🔥 FIX: Create a type that only includes practitioner roles
export type PractitionerRoleType = typeof PRACTITIONER_ROLES[number];

// Helper functions
export function isPractitionerRole(role: string): boolean {
  return PRACTITIONER_ROLES.includes(role as any);
}

export function isPatientRole(role: string): boolean {
  return role.toLowerCase() === SystemRoles.Patient;
}

// For UI display mapping
export const ROLE_DISPLAY_NAMES: Record<RoleType, string> = {
  [SystemRoles.SuperAdmin]: 'Super Admin',
  [SystemRoles.Admin]: 'Administrator',
  [SystemRoles.Practitioner]: 'Practitioner',
  [SystemRoles.ServiceProvider]: 'Service Provider',
  [SystemRoles.Doctor]: 'Doctor',
  [SystemRoles.Nurse]: 'Nurse',
  [SystemRoles.LabTechnician]: 'Lab Technician',
  [SystemRoles.Pharmacist]: 'Pharmacist',
  [SystemRoles.Receptionist]: 'Receptionist',
  [SystemRoles.Consultant]: 'Consultant',
  [SystemRoles.Cashier]: 'Cashier',
  [SystemRoles.Patient]: 'Patient'
};

// For dropdowns / selection dialogs
export const AVAILABLE_PRACTITIONER_ROLE_OPTIONS = [
  { code: SystemRoles.ServiceProvider, display: ROLE_DISPLAY_NAMES[SystemRoles.ServiceProvider] },
  { code: SystemRoles.Doctor, display: ROLE_DISPLAY_NAMES[SystemRoles.Doctor] },
  { code: SystemRoles.Nurse, display: ROLE_DISPLAY_NAMES[SystemRoles.Nurse] },
  { code: SystemRoles.LabTechnician, display: ROLE_DISPLAY_NAMES[SystemRoles.LabTechnician] },
  { code: SystemRoles.Pharmacist, display: ROLE_DISPLAY_NAMES[SystemRoles.Pharmacist] },
  { code: SystemRoles.Admin, display: ROLE_DISPLAY_NAMES[SystemRoles.Admin] },
  { code: SystemRoles.Receptionist, display: ROLE_DISPLAY_NAMES[SystemRoles.Receptionist] },
  { code: SystemRoles.Cashier, display: ROLE_DISPLAY_NAMES[SystemRoles.Cashier] },
  { code: SystemRoles.Consultant, display: ROLE_DISPLAY_NAMES[SystemRoles.Consultant] }
];

// 🔥 SOLUTION: Functions that should only accept practitioner roles
// Use PractitionerRoleType instead of RoleType

/**
 * Type guard that narrows RoleType to PractitionerRoleType
 */
export function asPractitionerRole(role: RoleType): PractitionerRoleType {
  if (isPractitionerRole(role)) {
    return role as PractitionerRoleType;
  }
  throw new Error(`Role '${role}' is not a practitioner role`);
}

/**
 * Safe conversion with null return instead of throwing
 */
export function toPractitionerRole(role: RoleType): PractitionerRoleType | null {
  return isPractitionerRole(role) ? (role as PractitionerRoleType) : null;
}

